import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ModelFavorite {
  provider: string;
  model: string;
}

interface ModelFavoritesStore {
  favorites: ModelFavorite[];
  addFavorite: (provider: string, model: string) => void;
  removeFavorite: (provider: string, model: string) => void;
  toggleFavorite: (provider: string, model: string) => void;
  isFavorite: (provider: string, model: string) => boolean;
  clearFavorites: () => void;
}

// Default favorites - commonly used models
const DEFAULT_FAVORITES: ModelFavorite[] = [
  { provider: 'anthropic', model: 'claude-3-5-sonnet-20241022' },
  { provider: 'openai', model: 'gpt-4o' },
  { provider: 'google', model: 'gemini-2.0-flash-exp' },
];

export const useModelFavoritesStore = create<ModelFavoritesStore>()(
  persist(
    (set, get) => ({
      favorites: DEFAULT_FAVORITES,

      addFavorite: (provider: string, model: string) => {
        const { favorites } = get();
        const exists = favorites.some(
          (fav) => fav.provider === provider && fav.model === model
        );
        if (!exists) {
          set({ favorites: [...favorites, { provider, model }] });
        }
      },

      removeFavorite: (provider: string, model: string) => {
        const { favorites } = get();
        set({
          favorites: favorites.filter(
            (fav) => !(fav.provider === provider && fav.model === model)
          ),
        });
      },

      toggleFavorite: (provider: string, model: string) => {
        const { isFavorite, addFavorite, removeFavorite } = get();
        if (isFavorite(provider, model)) {
          removeFavorite(provider, model);
        } else {
          addFavorite(provider, model);
        }
      },

      isFavorite: (provider: string, model: string) => {
        const { favorites } = get();
        return favorites.some(
          (fav) => fav.provider === provider && fav.model === model
        );
      },

      clearFavorites: () => {
        set({ favorites: [] });
      },
    }),
    {
      name: 'model-favorites-storage',
    }
  )
);
